import { CoreService } from './../interfaces/VirtualInterface';
import { Core, PaginationResponse, User, Virtual } from '../interfaces';
import { qsStringify } from '../shared/utils/QueryString';
import client from './client';

export enum CONTRIBUTION_STATUS {
  DRAFT,
  PROCESSING,
  ORCHESTRATING,
  VOTING,
  APPROVED,
  SERVICE_MINTED,
  AVAILABLE,
  REJECTED,
  SUBMITTED,
}

export interface CommitMetadata {
  filename?: string;
  contentType?: string;
}

export interface Commit {
  id: number;
  uid: string;
  key: string;
  subType: string;
  walletAddress: string;
  createdAt: string;
  updatedAt: string;
  encryptionKey?: string;
  filename?: string;
  metadata: CommitMetadata[];
  cid: string;
  contentType: string;
}

export interface Contribution {
  id: number;
  uid: string;
  status: string;
  type: string;
  subType: string;
  walletAddress: string;
  configUrl?: string;
  createdAt: string;
  updatedAt: string;
  proposalId?: string;
  metadata: Record<string, any>;
  version?: number;
  maturity?: number;
  contributionNftId?: string;
  contributionNftImageUrl?: string;
  contributionMintDate?: string;
  commit: Commit;
  core: Core;
  virtual: Virtual;
  user?: User;
  baseModel?: Contribution;
  baseDataset?: Contribution;
  coreService?: CoreService;
}

const createContribution = async (data: {
  coreId: number;
  virtual: number;
  baseModel?: number | null;
  baseDataset?: number | null;
  walletAddress: string;
  type: string;
  subType?: string;
  CoreService?: number;
  metadata?: Object;
  status: string;
}) => {
  const url = `/api/contributions`;
  const res = await client.post<{ data: Contribution }>(url, { data });

  return await getContribution(res.data.data.id);
};

const updateContribution = async (data: {
  contributionId: number;
  walletAddress?: string;
  type?: string;
  baseModel?: number | null;
  baseDataset?: number | null;
  subType?: string;
  metadata?: Object;
  status?: string;
}) => {
  const { contributionId, ...rest } = data;
  const url = `/api/contributions/${contributionId}`;
  const res = await client.put<{ data: Contribution }>(url, {
    data: { ...rest },
  });

  return await getContribution(res.data.data.id);
};

const getContribution = async (id: number) => {
  const url = `/api/contributions/${id}`;
  const res = await client.get<{ data: Contribution }>(url, {
    params: { populate: 'commit, core, baseDataset, baseModel, coreService' },
  });

  return res.data.data;
};

const getUploadToken = async (id: number, filename: string) => {
  const url = `/api/contributions/${id}/uploadToken`;
  const res = await client.get<{
    data: {
      uploadUrl: string;
    };
  }>(url, {
    params: { filename },
  });

  return res.data.data;
};

const updateContributionCommit = async (data: {
  id: number;
  cid: string;
  filename: string;
  contentType: string;
  encryptionKey?: string;
  metadata: CommitMetadata[];
}) => {
  const { id, ...rest } = data;

  const result = await client.put(`/api/commits/${id}`, {
    data: {
      ...rest,
    },
  });

  return result.data.data;
};

const getContributionList = async (page: number, pageSize: number) => {
  const url = `/api/contributions/me`;
  const res = await client.get<{
    data: Contribution[];
    meta: {
      pagination: {
        total: number;
        pageSize: number;
        page: number;
        pageCount: number;
      };
    };
  }>(url, {
    params: {
      populate:
        'commit, core, virtual, virtual.image, baseDataset, baseModel, coreService',
      sort: 'createdAt:desc',
      'pagination[pageSize]': pageSize,
      'pagination[page]': page,
    },
  });

  return res.data;
};

const getContributionsByVirtual = async (
  virtualId: number,
  coreId: number,
  page: number,
  pageSize: number
) => {
  const queryString = qsStringify({
    filters: {
      virtual: virtualId,
      status: {
        $notIn: ['draft', 'orchestrating', 'processing'],
      },
      core: coreId,
    },
    populate: [
      'commit',
      'core',
      'virtual.image',
      'baseDataset',
      'baseModel, coreService',
    ],
    sort: 'createdAt:desc',
    pagination: {
      page,
      pageSize,
    },
  });

  const res = await client.get<PaginationResponse<Contribution[]>>(
    `/api/contributions?${queryString}`
  );

  return res.data;
};

const getContributionByValidator = async () => {
  const url = `/api/contributions`;
  const res = await client.get<{
    data: Contribution[];
    meta: {
      pagination: {
        total: number;
        pageSize: number;
        page: number;
        pageCount: number;
      };
    };
  }>(url, {
    params: {
      populate: 'core, virtual, virtual.image , baseDataset, baseModel',
      sort: 'createdAt:desc',
      role: 'validator',
      'filters[status][$eq]': 'VOTING',
      // 'filter[metadata][]'
    },
  });

  return res.data;
};

const getClaimableRewardData = async (legacy?: boolean) => {
  const res = await client.get<{
    data: {
      virtualIds: bigint[];
      datasetNftIds: bigint[];
      modelNftIds: bigint[];
    };
  }>(`/api/contributions/claimable-rewards-data`, {
    params: {
      legacy,
    },
  });

  return res.data.data;
};

const getContributionForBaseModal = async (coreId: number, search?: string) => {
  let params;
  if (search) {
    params = {
      fields: ['id', 'metadata', 'status', 'type'],
      filters: {
        status: 'AVAILABLE',
        type: 'MODEL',
        core: { coreId: coreId },
        metadata: {
          $containsi: search,
        },
      },
    };
  } else {
    params = {
      fields: ['id', 'metadata', 'status', 'type'],
      filters: {
        status: 'AVAILABLE',
        type: 'MODEL',
        core: { coreId: coreId },
      },
    };
  }
  const res = await client.get<{
    data: Contribution[];
    meta: {
      pagination: {
        total: number;
        pageSize: number;
        page: number;
        pageCount: number;
      };
    };
  }>(`/api/contributions/options`, {
    params,
  });

  return res.data;
};
const getContributionForBaseDataset = async (
  coreId: number,
  search?: string,
  virtualId?: number
) => {
  let params;
  if (search) {
    params = {
      fields: ['id', 'metadata', 'status', 'type'],
      filters: {
        status: 'FINETUNE_REQUIRED',
        type: 'DATA',
        core: { coreId: coreId },
        metadata: {
          $containsi: search,
        },
        virtual: virtualId,
      },
    };
  } else {
    params = {
      fields: ['id', 'metadata', 'status', 'type'],
      filters: {
        status: 'FINETUNE_REQUIRED',
        type: 'DATA',
        core: { coreId: coreId },
        virtual: virtualId,
      },
    };
  }
  const res = await client.get<{
    data: Contribution[];
    meta: {
      pagination: {
        total: number;
        pageSize: number;
        page: number;
        pageCount: number;
      };
    };
  }>(`/api/contributions/options`, {
    params,
  });

  return res.data;
};
export {
  createContribution,
  updateContribution,
  updateContributionCommit,
  getContributionList,
  getContributionByValidator,
  getUploadToken,
  getClaimableRewardData,
  getContributionForBaseModal,
  getContributionForBaseDataset,
  getContributionsByVirtual,
};
